<script>
  import { onMount, getContext } from "svelte";
  import { some } from "overline/iterable";

  import { Modal } from "svelte-utilities";
  import FileIcon from "@local/assets/icons/file.svg";
  import FileIconPdf from "@local/assets/icons/file-pdf.svg";
  import WinBidProIcon from "@local/assets/icons/win-bid-pro.svg";
  import GSIcon from "@local/assets/icons/glazier-studio.svg";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";
  import ConfirmGsUpdate from "#src/lib/ConfirmGsUpdate.svelte";
  import { downloadJobBundle } from "#src/extensions/download-job-bundle";
  import { WBPVersion, GSVersion } from "#src/stores/ui.js";
  import eb from "#src/extensions/event-bus.js";
  import { profile } from "#src/stores/auth.js";

  export let title = "Download Report";
  export let group;
  export let types;
  export let name;
  export let rfq = null;
  export let quote = null;
  export let reportId = null;
  export const open = () => openModal;

  const edgeTreatments = getContext("edgeTreatments");

  let modal;
  let downloading = false;
  let includeData = false;
  let includeWBP = false;
  let includeGS = false;
  let includePDF = false;

  $: report = reportId ? $group?.item_reports[reportId] : null;
  $: disabled =
    (!includeData && !includeWBP && !includeGS && !includePDF) ||
    (includeGS && !$profile?.confirmed_gs_update_on);

  function hasAttachments(group) {
    return group?.attachments?.length > 0;
  }

  function hasDrawings(group) {
    if (group && group.items) {
      return some(group.items, (item) => !!item.drawing);
    }
    return false;
  }

  async function openModal() {
    modal.open();
  }

  async function downloadBundle() {
    downloading = true;

    const includeReportsExcel = $group.item_reports.order.map((id) => id === reportId && includeData);
    const includeReportsWBP = $group.item_reports.order.map((id) => id === reportId && includeWBP);
    const includeReportsGS = $group.item_reports.order.map((id) => id === reportId && includeGS);
    const includeReportsPdf = $group.item_reports.order.map((id) => id === reportId && includePDF);

    await downloadJobBundle(
      $group,
      types,
      { edgeTreatments: $edgeTreatments },
      {
        productQuote: $group?.project_type === "product",
        rfq,
        quote,
        filename: `${name}`,
        includeReportsExcel,
        includeReportsPdf,
        includeReportsWBP,
        includeReportsGS,
        wbpVersion: $WBPVersion,
        gsVersion: $GSVersion,
      },
      $profile,
    );
    modal.close();
    downloading = false;
  }

  onMount(() => {
    eb.on("open-report-download", openModal);

    return () => {
      eb.unsubscribe("open-report-download", openModal);
    };
  });
</script>

<Modal
  bind:this={modal}
  width="30rem"
  closeable
  on:confirm={downloadBundle}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Download", type: "confirm", style: "primary", closeOnClick: false, disabled },
  ]}>
  <div slot="title">{title}</div>
  <div slot="content" class="space-y-4">
    {#if downloading}
      <div class="flex gap-2 items-center">
        <div>Downloading...</div>
        <div class="relative w-8 h-8">
          <div class="absolute">
            <div class="animate-spin">
              <SpinnerIcon />
            </div>
          </div>
        </div>
      </div>
    {:else}
      <div class="font-bold">{report?.report_name || "Report"}</div>
      <div class="flex gap-4 justify-center items-center text-xs">
        <div class="flex flex-col items-center w-16 gap-2">
          <FileIcon />
          <div>Excel</div>
          <input type="checkbox" bind:checked={includeData} />
        </div>
        {#if report?.group_by === "opening"}
          <div class="flex flex-col items-center w-16 gap-2">
            <WinBidProIcon />
            <div>WinBidPro</div>
            <input type="checkbox" bind:checked={includeWBP} />
          </div>
          <div class="flex flex-col items-center w-16 gap-2">
            <GSIcon />
            <div>Glazier&nbsp;Studio</div>
            <input type="checkbox" bind:checked={includeGS} />
          </div>
        {/if}
        {#if report?.group_by !== "notes" && report?.group_by !== "linear"}
          <div class="flex flex-col items-center w-16 gap-2">
            <FileIconPdf />
            <div>PDF</div>
            <input type="checkbox" bind:checked={includePDF} />
          </div>
        {/if}
      </div>
      {#if includeGS && !$profile?.confirmed_gs_update_on}
        <ConfirmGsUpdate />
      {/if}
    {/if}
  </div>
</Modal>
